import { Component, Input, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { topBottomLocationInsightsRow } from '../../types/customer-types';
import { combineLatest, Subscription } from 'rxjs';
import { CustomerServiceInsightsService } from '../../services/customer-service-insights.service';
import { MatSelectChange } from '@angular/material/select';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { Router } from '@angular/router';
import { MixpanelService } from 'src/app/core/services/mixpanel.service';
import { AuthenticationService } from 'src/app/core/services/authentication.service';

@Component({
    selector: 'app-top-bottom-locations',
    templateUrl: './top-bottom-locations.component.html',
    styleUrls: ['./top-bottom-locations.component.scss'],
    standalone: false
})
export class TopBottomLocationsComponent implements OnInit {

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  pageNumberLabel: string = "_ of _";

  // constants
  private static readonly BY_BOTTOMUNITS: string = 'by-bottomunits';
  private static readonly BY_TOPUNITS: string = 'by-topunits';
  private static readonly BY_ALLUNITS: string = 'by-allunits';

  private subscription: Subscription = new Subscription();

  dropDownOptions : {option: string, value: {optionName: string, value: number}}[] = [
    {option:'5', value:{optionName: '5', value: 5}},
    {option:'10', value:{optionName: '10', value: 10}},
    {option:'5%', value:{optionName: '5%', value: 0}},
    {option:'10%', value:{optionName: '10%', value: 0}}
  ];
  preSelectedDropDownValue: { optionName: string; value: number } = {
    optionName: '5',
    value: 5,
  };
  tabSelected: string = 'by-bottomunits';
  data: topBottomLocationInsightsRow[] = [];
  isNoData: boolean = false;
  isLoading: boolean = false;
  sortDirections: { [key: string]: string } = {
    location: 'asc'
  };
  sortBy: string = 'location';
  sortDirection = 'desc';
  originalData: topBottomLocationInsightsRow[] = [];

  @Input() selectedPeriod : string ='';
  @Input() locationData : any;

  constructor(
    private _customerServiceInsightsService: CustomerServiceInsightsService,
    private router: Router,
    private _mixpanelService: MixpanelService,
    private _authenticationservice: AuthenticationService
  ) { }

  ngOnInit() {
    this.isLoading = true;
    this.preSelectedDropDownValue = this.dropDownOptions[0].value;

    if(this.locationData?.statusCode === 'OK'){
      this.originalData = this.locationData.data;
      this.dropDownOptions = this.locationData.dropdownOptions;
      this.preSelectedDropDownValue = this.dropDownOptions[0].value;
      this.getTableData();
      this.isNoData = false;
    }else{
      this.data = [];
      this.originalData = [];
      this.isNoData = true;
    }
    this.isLoading = false;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedPeriod']) {
      this.isLoading = true;
    }
  }

  changeTab(tab: string) {
    this.tabSelected = tab;
    if(tab === TopBottomLocationsComponent.BY_ALLUNITS){
      this.updatePageData(0, 5);
    }else{
      this.getTableData();
    }
    const tabName = this.tabSelected.replace('by-', '').replace(/(units)/, ' $1');
    this._mixpanelService.track(`PH ${tabName} tab changed`, { 
      ImpersonatedCustomer: this._authenticationservice.customerName??" ",
      ImpersonatedUser:  this._authenticationservice.userName?? "",
      TabName : tabName
    });
    
  }

  onSelectChange(selectChange?: MatSelectChange) {
    this.preSelectedDropDownValue = selectChange?.value;
    this.getTableData();
    const tabName = this.tabSelected.replace('by-', '').replace(/(units)/, ' $1');
    this._mixpanelService.track(`PH  ${this.preSelectedDropDownValue.optionName} selected from show dropdown `, { 
      ImpersonatedCustomer: this._authenticationservice.customerName??" ",
      ImpersonatedUser:  this._authenticationservice.userName??" ",
      dropdownValue: this.preSelectedDropDownValue.optionName,
      TabName : tabName
    });
  }

   getTableData() {
    let halfData = this.getSplitDataSet(this.tabSelected);
    this.data = halfData.slice(0, this.preSelectedDropDownValue.value);

  }

  //will return half of the data set depending on whether the top or bottom tab is selected
  private getSplitDataSet(tab: string): topBottomLocationInsightsRow[] {
    let middle: number = Math.round(this.originalData.length / 2);
    let splitData = [...this.originalData]; // Create a copy of the original data to avoid mutating it

    // Sort the data once in descending order
    splitData.sort((a, b) => b.performanceRating - a.performanceRating);

    //if only one row, show on all three tabs
    if(this.originalData.length === 1) {
      return splitData;
    }


    if (tab === TopBottomLocationsComponent.BY_TOPUNITS) {
        return splitData.slice(0, middle);
    } else if (tab === TopBottomLocationsComponent.BY_BOTTOMUNITS) {
        return splitData.slice(middle, splitData.length).reverse();
    } else if (tab === TopBottomLocationsComponent.BY_ALLUNITS) {
        // For all units, sort in ascending order
        return [...this.originalData].sort((a, b) => a.performanceRating - b.performanceRating);
    } else {
        return [];
    }
  }


  onPageChange(event: PageEvent) {
    this.getPageNumberLabel();
    this.updatePageData(event.pageIndex, event.pageSize);
  }

  getPageNumberLabel() {
    if (this.paginator !== undefined) {
      this.pageNumberLabel = `${this.paginator.pageIndex + 1} of ${this.paginator.getNumberOfPages()}`;
    }
    return this.pageNumberLabel;
  }

  updatePageData(pageIndex: number, pageSize: number) {
    const startIndex = pageIndex * pageSize;
    const endIndex = (pageIndex * pageSize) + pageSize;
    this.data = [...this.originalData].sort((a, b) => a.performanceRating - b.performanceRating)
                .slice(startIndex, endIndex);
  }

  getPerformanceRatingColor(performanaceRating: number): string {
    if (performanaceRating >= 90) {
      return '#39B54A';
    } else if (performanaceRating >= 70) {
      return '#ECC42F';
    } else {
      return '#DF3935';
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  viewDetailsClicked(elem: topBottomLocationInsightsRow) {
    this.router.navigateByUrl('/customer/location-details', {
      state: {
        locationName: elem.accountName,
        accountNumber: elem.accountNumber,
        geography: elem.city + ", " + elem.state
      }
    });
    this._mixpanelService.track(`PH View Details clicked`, { 
      ImpersonatedCustomer: this._authenticationservice.customerName??" ",
      ImpersonatedUser:  this._authenticationservice.userName??" ",
      AccountName: elem.accountName, 
      AccountNumber: elem.accountNumber  
    });
  }
}
